import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { UserService } from '../../users/userService';
import { CreateUserDto } from '../../users/dto/createUserDto';
import { ListUserDto } from '../../users/dto/listUserDto';
import { RestResponse } from '../../../shared/RestResponse';
import { CityService, City } from '../../cities/cityService';


@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.html',
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    MessageModule,
    ProgressSpinnerModule,
    DividerModule,
    CheckboxModule,
    SelectModule
  ]
})
export class CustomerFormComponent implements OnInit {
  userForm!: FormGroup;
  isLoading: boolean = false;
  errorMessage: string = '';
  isEditMode: boolean = false;
  userId: number | null = null;
  cities: City[] = [];
  expandedAddresses: Set<number> = new Set();

  constructor(
    private userService: UserService,
    private cityService: CityService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCities();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.userId = +params['id'];
        this.isEditMode = true;
        this.loadUser();
      } else {
        // No modo de criação, senha é obrigatória
        this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
        this.userForm.get('password')?.updateValueAndValidity();
        // Adiciona um endereço inicial
        this.addAddress();
      }
    });
  }

  private initializeForm(): void {
    this.userForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      roleId: [3], // Sempre CUSTOMER (ID 3)
      cnh: [''],
      phone: [''],
      isActive: [true],
      isBlocked: [false],
      addresses: this.fb.array([])
    });
  }

  get addressesFormArray(): FormArray {
    return this.userForm.get('addresses') as FormArray;
  }

  private createAddressFormGroup(): FormGroup {
    return this.fb.group({
      zipCode: ['', [Validators.required]],
      addressName: ['', [Validators.required]],
      number: [''],
      district: ['', [Validators.required]],
      cityId: [null, [Validators.required]]
    });
  }

  addAddress(): void {
    const newIndex = this.addressesFormArray.length;
    this.addressesFormArray.push(this.createAddressFormGroup());
    // Expande automaticamente o novo endereço
    this.expandedAddresses.add(newIndex);
  }

  removeAddress(index: number): void {
    if (this.addressesFormArray.length > 1) {
      this.addressesFormArray.removeAt(index);
      // Remove do Set e ajusta os índices
      this.expandedAddresses.delete(index);
      // Ajusta os índices dos endereços após o removido
      const newExpanded = new Set<number>();
      this.expandedAddresses.forEach(i => {
        if (i > index) {
          newExpanded.add(i - 1);
        } else if (i < index) {
          newExpanded.add(i);
        }
      });
      this.expandedAddresses = newExpanded;
    }
  }

  toggleAddress(index: number): void {
    if (this.expandedAddresses.has(index)) {
      this.expandedAddresses.delete(index);
    } else {
      this.expandedAddresses.add(index);
    }
  }

  isAddressExpanded(index: number): boolean {
    return this.expandedAddresses.has(index);
  }

  private loadCities(): void {
    this.cityService.list().subscribe({
      next: (response: RestResponse<City[]>) => {
        this.cities = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar cidades:', error);
        this.cities = [];
      }
    });
  }

  private loadUser(): void {
    if (this.userId) {
      this.isLoading = true;
      this.userService.getById(this.userId).subscribe({
        next: (response: RestResponse<ListUserDto>) => {
          this.userForm.patchValue({
            name: response.data.name,
            email: response.data.email,
            password: '', // Não carregamos a senha por segurança
            roleId: response.data.roleId,
            cnh: response.data.cnh,
            phone: response.data.phone || '',
            isActive: response.data.isActive !== undefined ? response.data.isActive : true,
            isBlocked: response.data.canRequestRide !== undefined ? !response.data.canRequestRide : false
          });
          
          // Carregar endereços se existirem
          if (response.data.addresses && response.data.addresses.length > 0) {
            // Limpar endereços existentes
            while (this.addressesFormArray.length !== 0) {
              this.addressesFormArray.removeAt(0);
            }
            this.expandedAddresses.clear();
            // Adicionar endereços do servidor
            response.data.addresses.forEach((address, index) => {
              this.addressesFormArray.push(this.fb.group({
                zipCode: [address.zipCode, [Validators.required]],
                addressName: [address.addressName, [Validators.required]],
                number: [address.number || ''],
                district: [address.district, [Validators.required]],
                cityId: [address.cityId, [Validators.required]]
              }));
              // Expande o primeiro endereço por padrão
              if (index === 0) {
                this.expandedAddresses.add(0);
              }
            });
          } else {
            // Se não houver endereços, adiciona um vazio
            if (this.addressesFormArray.length === 0) {
              this.addAddress();
            }
          }
          
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao carregar dados do usuário.';
          console.error('Erro ao carregar usuário:', error);
        }
      });
    }
  }

  onSubmit(): void {
    if (this.userForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      let userData: CreateUserDto = {
        ...this.userForm.value,
        addresses: this.addressesFormArray.value
      };
      
      // No modo de criação, sempre usar senha padrão "123456"
      if (!this.isEditMode) {
        userData.password = '123456';
      }

      if (this.isEditMode && this.userId) {
        this.updateUser(userData);
      } else {
        this.createUser(userData);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private createUser(userData: CreateUserDto): void {
    this.userService.create(userData).subscribe({
      next: (response: RestResponse<void>) => {
        this.isLoading = false;
        this.router.navigate(['/customers']);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Erro ao cadastrar usuário. Tente novamente.';
        console.error('Erro ao criar usuário:', error);
      }
    });
  }

  private updateUser(userData: CreateUserDto): void {
    if (this.userId) {
      this.userService.update(this.userId, userData).subscribe({
        next: (response: RestResponse<CreateUserDto>) => {
          this.isLoading = false;
          this.router.navigate(['/customers']);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao atualizar usuário. Tente novamente.';
          console.error('Erro ao atualizar usuário:', error);
        }
      });
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      if (key === 'addresses' && control instanceof FormArray) {
        control.controls.forEach(addressControl => {
          if (addressControl instanceof FormGroup) {
            Object.keys(addressControl.controls).forEach(addressKey => {
              addressControl.get(addressKey)?.markAsTouched();
            });
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  getFieldError(fieldName: string, addressIndex?: number): string {
    let field;
    if (addressIndex !== undefined) {
      field = this.addressesFormArray.at(addressIndex).get(fieldName);
    } else {
      field = this.userForm.get(fieldName);
    }
    
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter pelo menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['email']) {
        return `${this.getFieldLabel(fieldName)} deve ser um email válido`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'name': 'Nome',
      'email': 'Email',
      'roleId': 'Função',
      'cnh': 'CNH',
      'phone': 'Telefone',
      'isActive': 'Cliente Ativo',
      'isBlocked': 'Cliente Bloqueado',
      'zipCode': 'CEP',
      'addressName': 'Rua/Avenida',
      'number': 'Número',
      'district': 'Bairro',
      'cityId': 'Cidade'
    };
    return labels[fieldName] || fieldName;
  }

  onCancel(): void {
    this.router.navigate(['/customers']);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Cliente' : 'Cadastrar Cliente';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return this.isEditMode ? 'Atualizando...' : 'Cadastrando...';
    }
    return this.isEditMode ? 'Atualizar' : 'Cadastrar';
  }
}
